import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import {
  BUSINESS_RELATION_TYPE_LABELS,
  BusinessRelationSelectorModal,
  type BusinessRelationValueItem,
} from '@/modules/zy-crm/components/business-relation-selector-modal';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import { queryDictionaryItemOptions } from '@/services/bff/dictionary-management.api';
import {
  createMarketingCampaign,
  queryMarketingCampaignDetail,
  updateMarketingCampaign,
} from '@/services/bff/marketing-campaign.api';
import type { DictionaryOptionItem } from '@/services/bff/types/dictionary';
import type {
  CreateMarketingCampaignPayload,
  MarketingCampaignDetail,
  MarketingCampaignRelationType,
  MarketingCampaignStatus,
} from '@/services/bff/types/marketing-campaign';

const MemberPickerField = MemberPicker as unknown as React.FC<any>;
const RelationValueField: React.FC<{
  value?: BusinessRelationValueItem[];
  onChange?: (value: BusinessRelationValueItem[]) => void;
}> = () => null;
const SOURCE_CHANNEL_DICTIONARY_CODE = 'source-channels';
const CAMPAIGN_RELATION_TYPES: MarketingCampaignRelationType[] = [
  'customer',
  'contact',
  'lead',
  'opportunity',
];

const STATUS_OPTIONS: Array<{
  label: string;
  value: MarketingCampaignStatus;
}> = [
  { label: '草稿', value: 'draft' },
  { label: '筹备中', value: 'pending' },
  { label: '进行中', value: 'running' },
  { label: '已暂停', value: 'paused' },
  { label: '已关闭', value: 'closed' },
  { label: '已结束', value: 'finished' },
];

export type MarketingCampaignFormValues = {
  name: string;
  code?: string;
  type?: string;
  status?: MarketingCampaignStatus;
  startTime?: Dayjs;
  endTime?: Dayjs;
  ownerUserUuid?: string;
  sourceChannel?: string;
  budgetAmount?: number | null;
  goalLeadCount?: number | null;
  goalOpportunityAmount?: number | null;
  description?: string;
  remark?: string;
  relations?: BusinessRelationValueItem[];
};

export type MarketingCampaignEditModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  uuid?: string;
  onCancel: () => void;
  onSuccess?: (result: MarketingCampaignDetail) => void;
};

const toDateTimeString = (value?: Dayjs) => {
  if (!value) {
    return undefined;
  }
  return value.toISOString();
};

const trimText = (value?: string) => {
  const text = String(value || '').trim();
  return text || undefined;
};

export const buildMarketingCampaignPayload = (
  values: MarketingCampaignFormValues,
): CreateMarketingCampaignPayload => ({
  name: String(values.name || '').trim(),
  code: trimText(values.code),
  type: trimText(values.type),
  status: values.status || 'draft',
  startTime: toDateTimeString(values.startTime),
  endTime: toDateTimeString(values.endTime),
  ownerUserUuid: trimText(values.ownerUserUuid),
  sourceChannel: trimText(values.sourceChannel),
  budgetAmount:
    values.budgetAmount === undefined || values.budgetAmount === null
      ? undefined
      : String(values.budgetAmount),
  goalLeadCount:
    values.goalLeadCount === undefined || values.goalLeadCount === null
      ? undefined
      : Number(values.goalLeadCount),
  goalOpportunityAmount:
    values.goalOpportunityAmount === undefined ||
    values.goalOpportunityAmount === null
      ? undefined
      : String(values.goalOpportunityAmount),
  description: trimText(values.description),
  remark: trimText(values.remark),
  relations: values.relations?.map((item) => ({
    bizType: item.bizType as MarketingCampaignRelationType,
    bizUuid: item.bizUuid,
  })),
});

const mapDetailToFormValues = (
  detail?: MarketingCampaignDetail,
): MarketingCampaignFormValues => ({
  name: detail?.name || '',
  code: detail?.code,
  type: detail?.type,
  status: detail?.status || 'draft',
  startTime: detail?.startTime ? dayjs(detail.startTime) : undefined,
  endTime: detail?.endTime ? dayjs(detail.endTime) : undefined,
  ownerUserUuid: detail?.ownerUserUuid || detail?.ownerUuid,
  sourceChannel: detail?.sourceChannel || detail?.sourceChannelUuid,
  budgetAmount:
    detail?.budgetAmount === undefined || detail?.budgetAmount === null
      ? null
      : Number(detail.budgetAmount),
  goalLeadCount:
    detail?.goalLeadCount === undefined || detail?.goalLeadCount === null
      ? null
      : Number(detail.goalLeadCount),
  goalOpportunityAmount:
    detail?.goalOpportunityAmount === undefined ||
    detail?.goalOpportunityAmount === null
      ? null
      : Number(detail.goalOpportunityAmount),
  description: detail?.description,
  remark: detail?.remark,
});

export function MarketingCampaignEditModal({
  open,
  mode,
  uuid,
  onCancel,
  onSuccess,
}: MarketingCampaignEditModalProps) {
  const [form] = Form.useForm<MarketingCampaignFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [relationSelectorOpen, setRelationSelectorOpen] = useState(false);
  const [sourceChannelOptions, setSourceChannelOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const { ensureMembersLoaded } = useMemberDirectory();
  const selectedRelations = Form.useWatch('relations', form) || [];

  useEffect(() => {
    void ensureMembersLoaded().catch(() => undefined);
  }, [ensureMembersLoaded]);

  useEffect(() => {
    let active = true;
    void queryDictionaryItemOptions(SOURCE_CHANNEL_DICTIONARY_CODE)
      .then((list: DictionaryOptionItem[] = []) => {
        if (!active) {
          return;
        }
        setSourceChannelOptions(
          list
            .filter((item) => item.value && item.label)
            .map((item) => ({
              label: item.label,
              value: item.value,
            })),
        );
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setSourceChannelOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setLoading(false);
      return;
    }

    if (mode !== 'edit' || !uuid) {
      form.resetFields();
      form.setFieldsValue({ status: 'draft' });
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    void queryMarketingCampaignDetail(uuid)
      .then((detail) => {
        if (!active) {
          return;
        }
        form.setFieldsValue(mapDetailToFormValues(detail));
      })
      .catch((error: any) => {
        if (!active) {
          return;
        }
        message.error(error?.message || '市场活动详情加载失败');
      })
      .finally(() => {
        if (!active) {
          return;
        }
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [form, mode, open, uuid]);

  const title = useMemo(
    () => (mode === 'edit' ? '编辑市场活动' : '新建市场活动'),
    [mode],
  );

  const submitText = useMemo(() => (mode === 'edit' ? '保存' : '创建'), [mode]);

  const handleSubmit = async (values: MarketingCampaignFormValues) => {
    const payload = buildMarketingCampaignPayload(values);
    if (
      payload.startTime &&
      payload.endTime &&
      dayjs(payload.endTime).isBefore(dayjs(payload.startTime))
    ) {
      message.warning('结束时间不能早于开始时间');
      return;
    }

    setSubmitting(true);
    try {
      const result =
        mode === 'edit' && uuid
          ? await updateMarketingCampaign(uuid, payload)
          : await createMarketingCampaign(payload);
      message.success(
        mode === 'edit' ? '市场活动保存成功' : '市场活动创建成功',
      );
      onSuccess?.(result);
      form.resetFields();
      setRelationSelectorOpen(false);
    } catch (error: any) {
      message.error(
        error?.message ||
          (mode === 'edit' ? '市场活动保存失败' : '市场活动创建失败'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      className={`${modalStyles.modal} ${modalStyles.form}`}
      centered
      forceRender
      destroyOnHidden={false}
      open={open}
      title={title}
      onCancel={onCancel}
      footer={null}
      width={getResponsiveModalWidth('wideForm')}
      mask={{ closable: !submitting }}
    >
      <Form<MarketingCampaignFormValues>
        className={modalStyles.formGrid}
        form={form}
        layout="vertical"
        initialValues={{ status: 'draft' }}
        onFinish={handleSubmit}
      >
        <Divider titlePlacement="start" style={{ margin: '8px 0 16px' }}>
          <Typography.Text strong>基本信息</Typography.Text>
        </Divider>
        <Row gutter={[12, 0]}>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item
              name="name"
              label="活动名称"
              rules={[
                { required: true, message: '请输入活动名称' },
                { max: 128, message: '活动名称不能超过 128 个字符' },
              ]}
            >
              <Input maxLength={128} placeholder="请输入活动名称" />
            </Form.Item>
          </Col>
          <Col data-crm-field="short" xs={24} lg={12}>
            <Form.Item
              name="code"
              label="活动编号"
              rules={[{ max: 64, message: '活动编号不能超过 64 个字符' }]}
            >
              <Input maxLength={64} placeholder="请输入活动编号" />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item
              name="type"
              label="活动类型"
              rules={[{ max: 32, message: '活动类型不能超过 32 个字符' }]}
            >
              <Input maxLength={32} placeholder="请输入活动类型" />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item name="status" label="活动状态">
              <Select options={STATUS_OPTIONS} placeholder="请选择活动状态" />
            </Form.Item>
          </Col>
          <Col data-crm-field="short" xs={24} lg={12}>
            <Form.Item name="sourceChannel" label="主来源渠道">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={sourceChannelOptions}
                placeholder="请选择主来源渠道"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="short" xs={24} lg={12}>
            <Form.Item name="ownerUserUuid" label="负责人">
              <MemberPickerField adapter={orgDirectoryAdapter} />
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" style={{ margin: '4px 0 16px' }}>
          <Typography.Text strong>活动周期</Typography.Text>
        </Divider>
        <Row gutter={[12, 0]}>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item name="startTime" label="开始时间">
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item name="endTime" label="结束时间">
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" style={{ margin: '4px 0 16px' }}>
          <Typography.Text strong>投入与目标</Typography.Text>
        </Divider>
        <Row gutter={[12, 0]}>
          <Col data-crm-field="medium" xs={24} lg={8}>
            <Form.Item name="budgetAmount" label="活动预算">
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="请输入活动预算"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} lg={8}>
            <Form.Item name="goalLeadCount" label="目标线索数">
              <InputNumber
                min={0}
                precision={0}
                style={{ width: '100%' }}
                placeholder="请输入目标线索数"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} lg={8}>
            <Form.Item name="goalOpportunityAmount" label="目标商机金额">
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="请输入目标商机金额"
              />
            </Form.Item>
          </Col>
        </Row>

        {mode === 'create' ? (
          <>
            <Divider titlePlacement="start" style={{ margin: '4px 0 16px' }}>
              <Typography.Text strong>关联对象</Typography.Text>
            </Divider>
            <Form.Item name="relations" noStyle>
              <RelationValueField />
            </Form.Item>
            <Form.Item style={{ marginBottom: 20 }}>
              <div
                style={{
                  borderRadius: 8,
                  background: 'var(--ant-color-fill-quaternary)',
                  padding: '14px 16px',
                }}
              >
                <Space
                  orientation="vertical"
                  size={12}
                  style={{ width: '100%' }}
                >
                  <Space wrap>
                    <Button
                      type="primary"
                      color="primary"
                      variant="filled"
                      onClick={() => setRelationSelectorOpen(true)}
                    >
                      {selectedRelations.length
                        ? '调整关联对象'
                        : '选择关联对象'}
                    </Button>
                    {selectedRelations.length ? (
                      <Button
                        type="link"
                        onClick={() => form.setFieldValue('relations', [])}
                      >
                        清空
                      </Button>
                    ) : null}
                    <Typography.Text type="secondary">
                      可多选客户、联系人、线索和商机
                    </Typography.Text>
                  </Space>
                  {selectedRelations.length ? (
                    <Space wrap>
                      {CAMPAIGN_RELATION_TYPES.map((bizType) => {
                        const count = selectedRelations.filter(
                          (item: BusinessRelationValueItem) =>
                            item.bizType === bizType,
                        ).length;
                        return count ? (
                          <Tag key={bizType} color="processing">
                            {BUSINESS_RELATION_TYPE_LABELS[bizType]} {count}
                          </Tag>
                        ) : null;
                      })}
                    </Space>
                  ) : (
                    <Typography.Text type="secondary">
                      尚未选择关联对象
                    </Typography.Text>
                  )}
                </Space>
              </div>
            </Form.Item>
          </>
        ) : null}

        <Divider titlePlacement="start" style={{ margin: '4px 0 16px' }}>
          <Typography.Text strong>补充说明</Typography.Text>
        </Divider>
        <Row gutter={[12, 0]}>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item
              name="description"
              label="活动说明"
              rules={[{ max: 500, message: '活动说明不能超过 500 个字符' }]}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                placeholder="请输入活动说明"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} lg={12}>
            <Form.Item
              name="remark"
              label="备注"
              rules={[{ max: 500, message: '备注不能超过 500 个字符' }]}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                placeholder="请输入备注"
              />
            </Form.Item>
          </Col>
        </Row>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={loading}
          >
            {submitText}
          </Button>
        </Space>
      </Form>
      <BusinessRelationSelectorModal
        open={relationSelectorOpen}
        value={selectedRelations}
        defaultBizType="customer"
        allowedBizTypes={CAMPAIGN_RELATION_TYPES}
        title="选择市场活动关联对象"
        onCancel={() => setRelationSelectorOpen(false)}
        onOk={(items) => {
          form.setFieldValue('relations', items);
          setRelationSelectorOpen(false);
        }}
      />
    </Modal>
  );
}

export default MarketingCampaignEditModal;
